import { SetState } from 'ahooks/lib/createUseStorageState/index';
import { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { tAdvanceSearchLead } from '../../components/AdvanceSearch/AdvanceSearchModal.types.ts';
import { eDeleteFunnelProgressModal } from '../../pages/Pipe/modals/DeleteFunnelProgressModal/DeleteFunnelProgressModal.enum.ts';
import {
  tFunnelStatus,
  tFunnelsWithStages,
  tTagFilter,
} from '../../pages/Pipe/Pipe.types.ts';
import { eChannelType } from '../SocketsProvider/Sockets.enums.ts';
import { tStatusFilter } from '../SocketsProvider/Sockets.types.ts';
import { SyncConversationsEnum } from '../SyncConversationsProvider/SyncConversations.enum.ts';
import { tWorkspaceUserInfo } from '../WorkspaceProvider/Workspace.types.ts';

export enum eDateFilterType {
  CREATED_AT = 'created_at',
  LAST_ACTION_AT = 'last_action_at',
}

/**
 * A chat event the open thread should jump to and highlight (SRE-3328).
 *
 * Set by a message-search result in the inbox list and consumed exactly once
 * by MessageContainer. It rides here rather than on
 * `selectedLeadForChatModal` because the deep-link route rebuilds that lead
 * from the leads list (Messenger.hook) and would drop any extra field.
 *
 * `leadId` is not redundant: it is what tells the container the thread now on
 * screen is the one the event belongs to, instead of firing against whichever
 * conversation happened to be open when the click landed.
 *
 * Named `focusedChatEvent`, never `highlight*`: `highlightMessages` in
 * MessageBubbleMenu.hook is the unrelated "destacar mensaje" favourite.
 */
export type tFocusedChatEvent = {
  leadId: string;
  eventId: string;
  /**
   * The chat the event belongs to, when the producer knows it.
   *
   * `leadId` alone cannot tell two channels of the same contact apart: every
   * chat of a lead shares it, so while a channel switch is on its way the
   * OUTGOING channel's thread matches the lead and the jump would fire on it.
   * When this is set the container also waits for the thread on screen to be
   * this chat. Optional, so a producer that never switches channel (the inbox
   * result row, which opens the channel it names) keeps working unchanged.
   */
  chatId?: string;
  /**
   * The words to mark on the jumped-to bubble, as the producer's snippet
   * marked them (`<b>` of ts_headline). Set by the inbox result row, whose
   * jump would otherwise land on a bubble with nothing saying which words
   * matched. The in-chat search leaves it out: it marks its own active match
   * (ChatSearchHighlight.hook), for as long as the search is on it.
   */
  terms?: string[];
};

/** RET-2331: how the tag filter matches; 'any' is what it always did. */
export type tTagsMatch = 'any' | 'all';

/**
 * The surface that opened the WhatsApp API connection modal, for copy that
 * depends on where the flow started. Openers that pass none leave it `null`.
 */
export type tWhatsAppConnectionSource = 'dashboard' | 'onboarding';

/** Options for opening or closing the WhatsApp API connection modal. */
export type tWhatsAppConnectionModalOptions = {
  /** The surface that opens the modal; read on an open only. */
  source?: tWhatsAppConnectionSource;
  /**
   * The in-app path a control that closes the modal leads to, for an opener
   * that navigates itself once the modal closes; read on a close only.
   */
  exitDestination?: string;
};

export type tModalsContext = {
  addOptionsSecondLevelModalIsOpen: boolean;
  setAddOptionsSecondLevelModalIsOpen: (value: boolean) => void;
  addOptionsThirdLevelModalIsOpen: boolean;
  setAddOptionsThirdLevelModalIsOpen: (value: boolean) => void;
  addWorkspaceModelIsOpen: boolean;
  setAddWorkspaceModalIsOpen: (value: boolean) => void;
  connectionModalIsOpen: boolean;
  setConnectionModalIsOpen: (value: boolean) => void;
  disconnectionModalIsOpen: boolean;
  setDisconnectionModalIsOpen: (value: boolean) => void;
  metaConnectionModalIsOpen: boolean;
  setMetaConnectionModalIsOpen: (value: boolean) => void;
  /**
   * INT-1522 (JJ, channels review): MetaConnectionModalProvider is mounted
   * INSIDE ModalPresence, a sibling of `{children}` in ModalsProvider (see
   * Modals.provider.tsx) -- so a caller in `{children}` (e.g. the alert
   * CTA hook) can never reach that provider's own `setStep`/
   * `setForceReconnectStep` to request the reconnect step; it would always
   * resolve MetaConnectionModal.constants.ts's no-op initialState instead.
   * This flag lives here, in the ONE context both sides genuinely share,
   * so MetaConnectionModal can read it and set its own step accordingly.
   */
  metaConnectionModalReconnectRequested: boolean;
  setMetaConnectionModalReconnectRequested: (value: boolean) => void;
  metaPagesModalIsOpen: boolean;
  setMetaPagesModalIsOpen: (value: boolean) => void;
  whatsAppBusinessConnectionModalIsOpen: boolean;
  /** Where the open modal was opened from; `null` while closed or when the
   * opener passed no source. */
  whatsAppBusinessConnectionModalSource: tWhatsAppConnectionSource | null;
  /** Where the last close leads; `null` while open or when the close declared
   * none. */
  whatsAppBusinessConnectionModalExitDestination: string | null;
  /** Whether the person left the wizard for the current modal during this
   * opening; ModalsProvider sets it and clears it when the modal closes. */
  whatsAppBusinessConnectionModalIsLegacyChosen: boolean;
  setWhatsAppBusinessConnectionModalIsOpen: (
    value: boolean,
    options?: tWhatsAppConnectionModalOptions,
  ) => void;
  syncConversationsStatus: SyncConversationsEnum;
  setSyncConversationsStatus: Dispatch<SetStateAction<SyncConversationsEnum>>;
  showNoPaidModal: boolean;
  toggleNoPaidModal: (value: boolean) => void;
  showPastDueModal: boolean;
  togglePastDueModal: (value: boolean) => void;
  showTrialConfirmModal: boolean;
  toggleTrialConfirmModal: (value: boolean) => void;
  showPrevPastDueModal: boolean;
  togglePrevPastDueModal: (value: boolean) => void;
  toggleBankDetailsModal: (value: boolean) => void;
  showBankDetailsModal: boolean;
  changeServiceModalIsOpen: boolean;
  setChangeServiceModalIsOpen: (value: boolean) => void;
  addOptionsModalIsOpen: boolean;
  setAddOptionsModalIsOpen: (value: boolean) => void;
  deleteOptionModalIsOpen: boolean;
  setDeleteOptionModalIsOpen: (value: boolean) => void;
  changeFunnelModalIsOpen: boolean;
  setChangeFunnelModalIsOpen: (value: boolean) => void;
  showResendModal: boolean;
  setShowResendModal: (value: boolean) => void;
  advanceSearchModalIsOpen: boolean;
  setAdvanceSearchModalIsOpen: Dispatch<SetStateAction<boolean>>;
  selectedLeadForChatModal: tAdvanceSearchLead | null;
  setSelectedLeadForChatModal: Dispatch<
    SetStateAction<tAdvanceSearchLead | null>
  >;
  /** The chat event the thread should jump to once it is open; consumed once. */
  focusedChatEvent: tFocusedChatEvent | null;
  setFocusedChatEvent: Dispatch<SetStateAction<tFocusedChatEvent | null>>;
  forceDisconnectionModalIsOpen: boolean;
  setForceDisconnectionModalIsOpen: Dispatch<SetStateAction<boolean>>;
  openAdvanceFiltersModal: boolean;
  setOpenAdvanceFiltersModal: Dispatch<SetStateAction<boolean>>;
  messageReadFilter?: string | null;
  setMessageReadFilter: (value?: SetState<string> | undefined) => void;
  integrationFilter?: eChannelType | null;
  setIntegrationFilter: (value?: SetState<eChannelType> | undefined) => void;
  statusFilter?: tStatusFilter;
  setStatusFilter: (value?: SetState<tStatusFilter> | undefined) => void;
  tagsFilter?: Array<tTagFilter> | null;
  setTagsFilter: (value?: SetState<Array<tTagFilter>> | undefined) => void;
  assignedUserFilters?: Partial<tWorkspaceUserInfo> | null;
  setAssignedUserFilters: (
    value?: SetState<Partial<tWorkspaceUserInfo>> | undefined,
  ) => void;
  dateRange?: [Date | undefined, Date | undefined];
  setDateRange: (
    value?: SetState<[Date | undefined, Date | undefined]> | undefined,
  ) => void;
  dateFilterType?: eDateFilterType;
  setDateFilterType: (
    value?: SetState<eDateFilterType> | undefined,
  ) => void;
  /**
   * Directory only (RET-2331): the funnel and the stage the Directory is
   * filtered by, chosen from its Embudo and Etapa headers or from the top of
   * its Filtros panel. Both are 'all' when unset, the stage is a stage of the
   * chosen funnel, and the nil uuid as funnel is Bandeja de entrada.
   *
   * They live here, not in the Directory, because the panel renders from the
   * navbar, outside the Directory's provider: one filter, two places. They
   * are not stored, so a reload clears them, and the Directory resets them
   * when it unmounts or the workspace changes, so they never reach the
   * funnel board.
   */
  directoryFunnelFilter: string;
  setDirectoryFunnelFilter: Dispatch<SetStateAction<string>>;
  directoryStageFilter: string;
  setDirectoryStageFilter: Dispatch<SetStateAction<string>>;
  /**
   * Directory only (RET-2331): whether the tag filter wants leads with any of
   * the chosen tags (the default, what it always did) or with all of them.
   * Kept in the browser like the tags it qualifies, so a reload keeps the
   * same list; the funnel board never reads it.
   */
  directoryTagsMatch: tTagsMatch;
  setDirectoryTagsMatch: (value: tTagsMatch) => void;
  /** The Directory's funnels with their stages, for the panel. Null outside it. */
  directoryFunnels: tFunnelsWithStages[] | null;
  setDirectoryFunnels: Dispatch<SetStateAction<tFunnelsWithStages[] | null>>;
  /**
   * How many of the two Directory filters are on (0 to 2). Kept apart from
   * `activeFilterCount`, which the funnel board also reads as "filters on":
   * the Directory shows the sum of both.
   */
  directoryFilterCount: number;
  /**
   * INT-1596: the funnel board's "No entregados" chip, a transient work queue.
   * Held in memory only (never localStorage) and cleared when the board
   * unmounts, so it never follows the user into another session or into Chats.
   */
  undeliveredFilter: boolean;
  setUndeliveredFilter: (value: boolean) => void;
  activeFilterCount: number;
  arePipeFiltersActive: boolean;
  /** Clears every filter of the panel, the Directory's funnel and stage included. */
  resetFilters: () => void;
  integrationWhatsAppModalIsOpen: boolean;
  setIntegrationWhatsAppModalIsOpen: (show: boolean) => void;
  integrationInstagramModalIsOpen: boolean;
  setIntegrationInstagramModalIsOpen: (show: boolean) => void;
  integrationMessengerModalIsOpen: boolean;
  setIntegrationMessengerModalIsOpen: (show: boolean) => void;
  attachmentEditorModalIsOpen: boolean;
  setAttachmentEditorModalIsOpen: (show: boolean) => void;
  audioPermissionsModalIsOpen: boolean;
  setAudioPermissionsModalIsOpen: (show: boolean) => void;
  chatModalIsOpen: boolean;
  setChatModalIsOpen: (show: boolean) => void;
  maximizeModal: boolean;
  setMaximizeModal: (show: boolean) => void;
  currentFunnelStatus: tFunnelStatus[];
  setCurrentFunnelStatus: Dispatch<SetStateAction<tFunnelStatus[]>>;
  triggerDirectoryReload: boolean;
  setTriggerDirectoryReload: React.Dispatch<React.SetStateAction<boolean>>;
  showSubscribeIntegrationsModal: boolean;
  setShowSubscribeIntegrationsModal: (show: boolean) => void;
  showSubscribeAddUsersModal: boolean;
  setShowSubscribeAddUsersModal: (show: boolean) => void;
  showSubscribeImportLeadsModal: boolean;
  setShowSubscribeImportLeadsModal: (show: boolean) => void;
  changesDataModalIsOpen: boolean;
  setChangesDataModalIsOpen: (show: boolean) => void;
  showDeleteFunnelModal: eDeleteFunnelProgressModal;
  setShowDeleteFunnelModal: Dispatch<
    SetStateAction<eDeleteFunnelProgressModal>
  >;
  showArchiveLeadsModal: boolean;
  setShowArchiveLeadsModal: Dispatch<SetStateAction<boolean>>;
  tiktokConnectionModalIsOpen: boolean;
  setTiktokConnectionModalIsOpen: (value: boolean) => void;
  tagOpsInFlight: MutableRefObject<number>;
};

export type tModalsProviderProps = { children: React.ReactNode };
